/**
 * product-metadata-adapter.ts — product_metadata 真 DTO（snake_case）→ 页面行（camelCase）塑形
 *
 * 字段映射（ProductMetadataResponse → ProductMetadataRow，per-通道 #306）：
 *   app_id→appId / vendor_product_id→vendorProductId / payment_method→paymentMethod（通道）/ product_type→productType /
 *   subscription_effect→subscriptionEffect（订阅效果，服务端已归一为 COIN_PERIOD_GRANT / VIP_READING）/
 *   subscription_period→subscriptionPeriod / name→name（运营识别名）/ title_i18n→titleI18n（保留原 map 供编辑回填）+
 *   title（解析展示串）/ coin_amount→coinAmount / bonus_amount→bonusAmount（金额均保留非负整数串，不转 number——
 *   上界 int8 超 2^53，Number() 会丢精度）/ bonus_ratio→bonusRatio（decimal string 原样，不转 number）/
 *   bonus_expire_days→bonusExpireDays / is_active→isActive / updated_at||created_at→updatedAt /
 *   consumable_effect→consumableEffect（一次性产品权益效果）/ consumable_effect_params→consumableEffectParams /
 *   channel_prices_summary→channelPricesSummary（框架通道行的只读价格汇总，IAP / STRIPE 恒 null，A8 §1）。
 *
 * 金额语义：coin/bonus 是「发多少 coin/bonus」的计数（非货币金额），序列化为非负整数串；`COIN_PERIOD_GRANT`
 * 订阅为每计费周期一次性发放量。冻结列 daily_grant_amount 不在响应里（A8 §1），本层不再承接。
 * 展示用 groupDigits 做千分位（BigInt 安全的纯字符串分组，不经 Number）。
 */

import type {
  ChannelPriceSummaryItem,
  ConsumableEffect,
  ConsumableEffectParams,
  ProductMetadataResponse,
  ProductType,
  ProductValidationIssue,
  ReadingPassTierId,
  SettlementChannel,
  SubscriptionEffect,
  SubscriptionPeriod,
  TitleI18n,
} from '@novel-admin/shared';
import { isReadingPassTierId, isSettlementChannel } from '@novel-admin/shared';

/** product_metadata 页面行（PaywallPage ProductMirror tab 消费；含编辑回填所需的原始字段；per-通道 #306） */
export interface ProductMetadataRow {
  id: string;
  appId: string;
  vendorProductId: string;
  /** 客户端匹配 + 跨渠道分组键（product 级）；null = 待运营从 Adapty Dashboard 补（NULL 不进 C 端 serving）*/
  accessLevelId: string | null;
  /** 通道：IAP / STRIPE（per-通道，同 product 每通道一行）*/
  paymentMethod: SettlementChannel;
  /**
   * STRIPE direct Checkout 的 Price 路由标识（`price_...`）；IAP 行恒 null。
   * **不参与可售判定**——缺它不影响 Adapty 商品展示与付款后履约，只是该行不能发起 direct Checkout（A8 §9.6）。
   */
  stripePriceId: string | null;
  productType: ProductType;
  /**
   * 订阅效果（70 §2，product 级、创建后不可换）；CONSUMABLE 行恒 null。服务端已把存量 COIN_DAILY_BONUS
   * 归一为 COIN_PERIOD_GRANT，本层只见对外闭集。
   */
  subscriptionEffect: SubscriptionEffect | null;
  subscriptionPeriod: SubscriptionPeriod | null;
  /** 运营识别名（product 级）*/
  name: string;
  /** 原始多语言 map（编辑弹窗回填用） */
  titleI18n: TitleI18n;
  /** 解析后的展示标题（zh→en→首个非空→vendorProductId 兜底） */
  title: string;
  /** 非负整数串（coin 计数，保留串避免 int8 超 2^53 丢精度） */
  coinAmount: string;
  bonusAmount: string;
  /** 该通道单产品赠送比例（**响应派生值**，decimal string 如 "0.30"；CONSUMABLE 与 SUBSCRIPTION 同口径）*/
  bonusRatio: string;
  bonusExpireDays: number;
  /**
   * 一次性产品的权益效果（dev-docs `03-数据字典/70-产品配置.md` §2 列，闭集见 §2.5）；SUBSCRIPTION 行恒 null。
   * CONSUMABLE 行为 null = 该行履约无从分派、C 端不可售。**创建后不可改**（A8 §3 `.strict()` 拒传）。
   */
  consumableEffect: ConsumableEffect | null;
  /** 该效果的参数（§2.5 逐效果 shape）；`COIN_GRANT` / 无效果时为 `{}` */
  consumableEffectParams: ConsumableEffectParams;
  isActive: boolean;
  /** 首次发布时刻（ISO）；null = 从未发布的草稿。配对任一行非空即锁时长 */
  firstPublishedAt: string | null;
  /** 当前是否可进入 C 端新购链路（读时派生，异于运营开关 isActive；#465）*/
  isServeable: boolean;
  /**
   * STRIPE CONSUMABLE（发币）与 STRIPE `COIN_PERIOD_GRANT` SUBSCRIPTION 当前派生 bonus 下限（整数串，
   * 同一公式 `IAP bonus + ceil(IAP coin × ratio)`）；其他行 / 无基线 null
   */
  derivedMinimumBonusAmount: string | null;
  /** 不可售原因闭集（可售时 []）*/
  validationIssues: ProductValidationIssue[];
  /**
   * 框架通道行的只读价格汇总（A8 §1，与 §10.3 GET `prices[]` 同源，币种升序）：`[]` = 未定价；
   * IAP / STRIPE 行恒 null。金额保留 minor units 整数串，不经 Number。写价格只经 §10.3。
   */
  channelPricesSummary: ChannelPriceSummaryItem[] | null;
  updatedAt: string;
}

/** 结算通道 → 中文标签（应用内购买 / 三方支付）；框架通道用正式展示名 */
const PAYMENT_METHOD_LABEL: Record<SettlementChannel, string> = {
  IAP: '应用内（IAP）',
  STRIPE: '三方（STRIPE）',
  PAYERMAX: '三方（PayerMax）',
  AIRWALLEX: '三方（Airwallex）',
};
export function paymentMethodLabel(m: SettlementChannel): string {
  // 闭集外取值（软删历史行 / 新登记未发版的通道）原样显示，不冒充成 IAP
  return isSettlementChannel(m) ? PAYMENT_METHOD_LABEL[m] : m;
}

/** product_type → 中文标签（充值 / 订阅） */
export function productTypeLabel(t: ProductType): string {
  return t === 'SUBSCRIPTION' ? '订阅' : '充值';
}

/** subscription_period → 中文标签；null（非订阅 / 未配置）→ '—' */
export function subscriptionPeriodLabel(p: SubscriptionPeriod | null): string {
  switch (p) {
    case 'WEEKLY':
      return '周';
    case 'MONTHLY':
      return '月';
    case 'QUARTERLY':
      return '季';
    case 'YEARLY':
      return '年';
    default:
      return '—';
  }
}

/** subscription_effect → 中文标签（70 §2 对外闭集）；null（充值行）→ '—' */
export function subscriptionEffectLabel(effect: SubscriptionEffect | null): string {
  switch (effect) {
    case 'COIN_PERIOD_GRANT':
      return '周期发币';
    case 'VIP_READING':
      return 'VIP 畅读';
    default:
      return '—';
  }
}

/** consumable_effect → 中文标签（§2.5 闭集）；null（订阅 / 未登记）→ '—' */
export function consumableEffectLabel(effect: ConsumableEffect | null): string {
  switch (effect) {
    case 'COIN_GRANT':
      return '发币';
    case 'READING_PASS_CARD':
      return '小时阅读卡';
    case 'NOVEL_PURCHASE':
      return '书籍买断';
    default:
      return '—';
  }
}

/**
 * 取 `READING_PASS_CARD` 行的产品位身份 `tier_id`（§2.5「身份与权益分离」：客户端寻址键）。
 * 非本效果、或 params 的 tier_id 为空 / 含空白 → null（不猜、不按时长反推产品位）。
 */
export function readingPassTierIdOf(row: {
  consumableEffect: ConsumableEffect | null;
  consumableEffectParams: ConsumableEffectParams;
}): ReadingPassTierId | null {
  if (row.consumableEffect !== 'READING_PASS_CARD') return null;
  const tierId = row.consumableEffectParams?.tier_id;
  return isReadingPassTierId(tierId) ? tierId : null;
}

/**
 * 取 `READING_PASS_CARD` 行的权益时长 `duration_seconds`（§2.5：整数 `> 0`）。
 * 非本效果 / 非正整数 → null（不合 shape 的值不展示成一个像样的时长，掩盖配置错误）。
 */
export function readingPassDurationSecondsOf(row: {
  consumableEffect: ConsumableEffect | null;
  consumableEffectParams: ConsumableEffectParams;
}): number | null {
  if (row.consumableEffect !== 'READING_PASS_CARD') return null;
  const seconds = row.consumableEffectParams?.duration_seconds;
  return typeof seconds === 'number' && Number.isSafeInteger(seconds) && seconds > 0
    ? seconds
    : null;
}

/** 时长秒数 → 运营可读串（整小时/整分钟优先，否则原样秒）；null → '—' */
export function formatDurationSeconds(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds % 3600 === 0) return `${seconds / 3600} 小时`;
  if (seconds % 60 === 0) return `${seconds / 60} 分钟`;
  return `${seconds} 秒`;
}

/** 解析展示标题：admin UI 中文优先（zh→en→首个非空值→兜底串） */
export function resolveTitle(titleI18n: TitleI18n, fallback: string): string {
  const zh = titleI18n.zh?.trim();
  if (zh) return zh;
  const en = titleI18n.en?.trim();
  if (en) return en;
  for (const v of Object.values(titleI18n)) {
    const t = v?.trim();
    if (t) return t;
  }
  return fallback;
}

/**
 * 非负整数串千分位分组（BigInt 安全：纯字符串操作，不经 Number → 不丢 int8 精度）。
 * 非法/空串原样返回（防御）。
 */
export function groupDigits(intStr: string): string {
  if (!/^\d+$/.test(intStr)) return intStr;
  return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function adaptProductMetadata(dto: ProductMetadataResponse): ProductMetadataRow {
  return {
    id: dto.id,
    appId: dto.app_id,
    vendorProductId: dto.vendor_product_id,
    accessLevelId: dto.access_level_id,
    paymentMethod: dto.payment_method,
    stripePriceId: dto.stripe_price_id,
    productType: dto.product_type,
    subscriptionEffect: dto.subscription_effect,
    subscriptionPeriod: dto.subscription_period,
    name: dto.name,
    titleI18n: dto.title_i18n,
    title: resolveTitle(dto.title_i18n, dto.vendor_product_id),
    coinAmount: dto.coin_amount,
    bonusAmount: dto.bonus_amount,
    bonusRatio: dto.bonus_ratio,
    bonusExpireDays: dto.bonus_expire_days,
    consumableEffect: dto.consumable_effect,
    // jsonb 原样保留（编辑回填 + 产品位展示）；契约保证是 object，防御性兜底空对象
    consumableEffectParams: dto.consumable_effect_params ?? {},
    isActive: dto.is_active,
    firstPublishedAt: dto.first_published_at,
    isServeable: dto.is_serveable,
    derivedMinimumBonusAmount: dto.derived_minimum_bonus_amount,
    validationIssues: dto.validation_issues,
    channelPricesSummary: dto.channel_prices_summary,
    updatedAt: dto.updated_at || dto.created_at,
  };
}
